import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { PrimaryCta, SecondaryCta, H_SECTION, SUBLINE } from "@/components/marketing/Sections";
import { usePlatformPricing } from "@/hooks/usePlatformPricing";

/*
 * Pricing, Apple-style: enormous type, no cards, no icons.
 * Platform Economics: los importes vienen SIEMPRE de Admin → Economics
 * (usePlatformPricing) — jamás cifras hardcodeadas ni copias por idioma.
 */
export const PricingSection = ({ showCta = true, showImage = true, HeadingTag = "h2" }) => {
  const { t } = useTranslation();
  const { fee, commission } = usePlatformPricing();
  return (
    <section className="bg-[#f5f5f7] pt-20 lg:pt-28" data-testid="pricing-section">
      <div className="max-w-[980px] mx-auto px-6 text-center flex flex-col items-center gap-4">
        <HeadingTag className={`${H_SECTION} text-[#1d1d1f]`}>{t("mk.pricingHead")}</HeadingTag>
        <p className={`${SUBLINE} text-[#6e6e73] max-w-2xl`}>{t("pricing.title")}</p>
        <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-12 sm:gap-24">
          <div className="flex flex-col items-center gap-2" data-testid="pricing-fee-card">
            <span className="text-6xl lg:text-[88px] font-semibold tracking-[-0.02em] text-[#1d1d1f] tabular-nums leading-none">
              {fee ?? "·"}
            </span>
            <span className="text-sm text-[#6e6e73]">{t("pricing.feeLabel")}</span>
          </div>
          <div className="flex flex-col items-center gap-2" data-testid="pricing-commission-card">
            <span className="text-6xl lg:text-[88px] font-semibold tracking-[-0.02em] text-[#1d1d1f] tabular-nums leading-none">
              {commission ?? "·"}
            </span>
            <span className="text-sm text-[#6e6e73]">{t("pricing.commissionLabel")}</span>
          </div>
        </div>
        <p className="mt-8 text-sm text-[#6e6e73] max-w-xl">{t("pricing.note")}</p>
        {showCta && (
          <div className="mt-4 flex flex-wrap items-center justify-center gap-4">
            <PrimaryCta small to="/create" testid="pricing-cta-create">{t("mk.cta")}</PrimaryCta>
            <SecondaryCta small to="/pricing" testid="pricing-cta-more">{t("mk.pricingCta")}</SecondaryCta>
          </div>
        )}
      </div>
      <div className="pb-20 lg:pb-28" aria-hidden="true" />
    </section>
  );
};

/* Three supporting text columns used on the pricing page — plain text, no boxes. */
export const PricingDetails = () => {
  const { t } = useTranslation();
  return (
    <section className="bg-white" data-testid="pricing-details">
      <div className="max-w-[980px] mx-auto px-6 py-20 lg:py-28 grid grid-cols-1 md:grid-cols-3 gap-12">
        {["p1", "p2", "p3"].map((k) => (
          <div key={k} className="flex flex-col gap-2">
            <h3 className="text-[17px] font-semibold text-[#1d1d1f]">{t(`pricing.${k}Title`)}</h3>
            <p className="text-sm text-[#6e6e73] leading-relaxed">{t(`pricing.${k}Desc`)}</p>
          </div>
        ))}
      </div>
    </section>
  );
};
